import React, { Suspense, lazy } from 'react'
import {
  Heading,
  Paragraph,
  Spinner,
  Tabset,
  getIsDevToolsEnabled,
  SilentErrorBoundary,
} from '@quickmd-llc/design-system'
import type { Tab } from '@quickmd-llc/design-system'
import { DailyVisitProvider } from '../daily/components/DailyVisitProvider'
import { OnlineVisitsSection } from './components/OnlineVisitsSection'
import { ReadyToStartVisitPopup } from './components/ReadyToStartVisitPopup'
import { AllSetEmptyState } from './components/AllSetEmptyState'
import { useAsyncVisitStart } from './hooks/useAsyncVisitStart'
import { UnsupportedPlatformPopUp } from './components/UnsupportedPlatformPopUp'
import { VisitPopUp } from '../VisitCallRoom/visit-popup/VisitPopUp'
import { HardwarePermissionsPopUp } from './components/HardwarePermissionsPopUp'
import { useGetProfile, getGetProfileQueryKey } from 'api/generated/profile/profile'
import { IncompleteSection } from './components/IncompleteSection'
import { ScheduleSection } from './components/ScheduleSection'
import moment from 'moment'
import { PageLayout } from 'components/page-layouts/PageLayout'
import { AnnouncementPlacement } from 'components/announcements/AnnouncementPlacement'
import { RoomLoadingSpinner } from '../daily/components/RoomLoadingSpinner'
import { useVisitLists } from './hooks/useVisitLists'
import { useIsDashboardActive } from './hooks/useIsDashboardActive'
import { usePatientVisitInfo } from './hooks/usePatientVisitInfo'
import { useVisitActions } from './hooks/useVisitActions'
import { useDashboardDocumentTitle } from './hooks/useDashboardDocumentTitle'
import { client } from 'api/components/queryClient'
import {
  type PatientStaffApiOutContract,
  type VisitDoctorApiOutContract,
  type WaitingRoomV2VisitListItemDoctorApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { VisitMethods } from 'constants/VisitMethods'
import { usePutVisitsVisitIdEnd } from 'api/generated/visit/visit'
import { isAsyncProvider } from 'utils/asyncVisitTopics'
import { excludeVisitsByIds, pruneRemovedMask, pruneStartMask } from './utils'
import { makeBoundaryErrorReporter } from 'utils/sentryUtility'

// Module scope so the handler identity is stable across renders.
const reportDevToolsBoundaryError = makeBoundaryErrorReporter('dashboard-dev-tools')

const LazyVisitLifecycleDebugger = lazy(() =>
  import('features/devTools/components/VisitLifecycleDebugger').then((module) => ({
    default: module.VisitLifecycleDebugger,
  })),
)

const LazyDashboardStateInspector = lazy(() =>
  import('features/devTools/components/DashboardStateInspector').then((module) => ({
    default: module.DashboardStateInspector,
  })),
)

export const Dashboard = () => {
  const [isLoadingVisit, setIsLoadingVisit] = React.useState<boolean>(false)
  const profile = useGetProfile({
    query: { enabled: !client.getQueryData(getGetProfileQueryKey()) },
  }).data
  const firstName = profile?.FirstName
  const [showPermissionDeniedFor, setShowPermissionDeniedFor] = React.useState<'call' | 'video' | undefined>(undefined)
  const [showHardwareDeniedFor, setShowHardwareDeniedFor] = React.useState<'call' | 'video' | undefined>(undefined)
  const [showUnsupportedPlatformFor, setShowUnsupportedPlatformFor] = React.useState<'call' | 'video' | undefined>(
    undefined,
  )
  const [selectedVisitInformation, setSelectedVisitInformation] = React.useState<VisitDoctorApiOutContract | undefined>(
    undefined,
  )
  const [selectedVisit, setSelectedVisit] = React.useState<WaitingRoomV2VisitListItemDoctorApiOutContract | undefined>(
    undefined,
  )
  const [selectedPatientInformation, setSelectedPatientInformation] = React.useState<
    PatientStaffApiOutContract | undefined
  >(undefined)
  const [showVisitPopUp, setShowVisitPopUp] = React.useState<boolean>(false)
  const [isPrepMode, setIsPrepMode] = React.useState<boolean>(false)
  // VisitIds just deferred by this provider. We optimistically render their online-visit rows as
  // "Start" until the eventually-consistent backend list reflects the release (see reconcile
  // effect below), avoiding the lag while we wait for the ASYNC_VISIT_RELEASED MQTT refresh.
  const [optimisticStartIds, setOptimisticStartIds] = React.useState<Set<string>>(new Set())
  // VisitIds just completed by this provider. We optimistically drop their online-visit rows (and
  // the "Review" tab badge count) until the eventually-consistent backend list reflects the
  // completion (see reconcile effect below), avoiding the lag while we wait for the
  // forceUpdateWaitingRoom MQTT refresh. This also prevents interacting with the stale completed
  // row, which would fail-claim and surface the wrong "just claimed by another provider" toast.
  const [optimisticRemovedIds, setOptimisticRemovedIds] = React.useState<Set<string>>(new Set())
  // Tracks the list identity the optimistic masks were last reconciled against, so we can prune
  // them during render (see below) instead of in an effect.
  const [reconciledList, setReconciledList] = React.useState<WaitingRoomV2VisitListItemDoctorApiOutContract[] | null>(
    null,
  )
  const isDashboardActive = useIsDashboardActive(showVisitPopUp)
  const visitLists = useVisitLists({ isActive: isDashboardActive })
  const {
    scheduleList,
    pendingList,
    onlineVisitsList,
    nextVisit,
    justChangeTags,
    dismissedVisits,
    dismissVisit,
    isLoading: isWaitingRoomLoading,
  } = visitLists
  const patientWithVisitInfo = usePatientVisitInfo(selectedPatientInformation, selectedVisitInformation, selectedVisit)

  // All upcoming (today+) scheduled visits, earliest first — lets the visit popup's timer
  // count down to the visit *after* the one that's open (QMD-3816).
  const upcomingVisits = React.useMemo(
    () =>
      [nextVisit, ...scheduleList]
        .filter((v): v is WaitingRoomV2VisitListItemDoctorApiOutContract => Boolean(v))
        .sort((a, b) => new Date(a.DateTime).getTime() - new Date(b.DateTime).getTime()),
    [nextVisit, scheduleList],
  )

  const handleVisitDeferred = React.useCallback((visitId: string) => {
    setOptimisticStartIds((prev) => {
      if (prev.has(visitId)) return prev
      const next = new Set(prev)
      next.add(visitId)
      return next
    })
  }, [])

  const handleVisitCompleted = React.useCallback((visitId: string) => {
    setOptimisticRemovedIds((prev) => {
      if (prev.has(visitId)) return prev
      const next = new Set(prev)
      next.add(visitId)
      return next
    })
  }, [])

  // Reconcile the optimistic masks against the freshly-fetched list during render — React's
  // "adjust state when a prop changes" pattern — rather than in an effect (which adds an extra
  // commit and trips react-hooks/set-state-in-effect). When the list identity changes we prune
  // both masks once: pruneStartMask keeps a just-deferred id only while its visit is still
  // 'pending' (so a later re-claim shows "Continue" rather than a stale "Start"); pruneRemovedMask
  // drops a just-completed id once the visit leaves the queue. Both return the same reference when
  // nothing changed, so this settles in a single extra render and never loops.
  if (reconciledList !== onlineVisitsList) {
    setReconciledList(onlineVisitsList)
    setOptimisticStartIds((prev) => pruneStartMask(prev, onlineVisitsList))
    setOptimisticRemovedIds((prev) => pruneRemovedMask(prev, onlineVisitsList))
  }

  // The online-visits list the section AND the "Review" tab badge both read, with just-completed
  // visits masked out so the row disappears and the count updates together.
  const visibleOnlineVisitsList = React.useMemo(
    () => excludeVisitsByIds(onlineVisitsList, optimisticRemovedIds),
    [onlineVisitsList, optimisticRemovedIds],
  )

  useDashboardDocumentTitle({
    isVisitPopUpOpen: showVisitPopUp,
    patient: selectedPatientInformation,
  })

  const { mutate: endVisit } = usePutVisitsVisitIdEnd()

  const {
    onStartVisitClick,
    onCloseVisitPopUp,
    onPrepVisitClick,
    onStartAsyncVisitClick,
    onContinueAsyncVisitClick,
    guardAgainstOtherTab,
  } = useVisitActions({
    setSelectedVisit: (v) => setSelectedVisit(v),
    setSelectedVisitInformation: (info) => setSelectedVisitInformation(info),
    setSelectedPatientInformation: (info) => setSelectedPatientInformation(info),
    setShowVisitPopUp: (open) => setShowVisitPopUp(open),
    setIsLoadingVisit: (loading) => setIsLoadingVisit(loading),
    setShowPermissionDeniedFor: (m) => setShowPermissionDeniedFor(m),
    setShowHardwareDeniedFor: (m) => setShowHardwareDeniedFor(m),
    isPopupOpen: showVisitPopUp,
    setShowUnsupportedPlatformFor: (m) => setShowUnsupportedPlatformFor(m),
    setIsPrepMode: (isPrep) => setIsPrepMode(isPrep),
  })

  const asyncStart = useAsyncVisitStart(onStartAsyncVisitClick, guardAgainstOtherTab)

  type QueueTabValue = 'schedule' | 'incomplete' | 'online'
  // Only providers licensed for async (online MOUD) visits get the Online tab.
  const showOnlineTab = isAsyncProvider(profile)
  const QUEUE_TAB_ORDER: QueueTabValue[] =
    showOnlineTab ? ['schedule', 'incomplete', 'online'] : ['schedule', 'incomplete']
  const isQueueTabValue = (v: string): v is QueueTabValue => QUEUE_TAB_ORDER.some((tab) => tab === v)

  const scheduleCount = scheduleList.length + (nextVisit ? 1 : 0)
  const incompleteCount = pendingList.length
  const onlineCount = visibleOnlineVisitsList.length
  const counts: Record<QueueTabValue, number> = {
    schedule: scheduleCount,
    incomplete: incompleteCount,
    online: onlineCount,
  }

  const [selectedTab, setSelectedTab] = React.useState<QueueTabValue | null>(null)
  const firstNonEmpty = QUEUE_TAB_ORDER.find((v) => counts[v] > 0) ?? null
  const activeTab = selectedTab && counts[selectedTab] > 0 ? selectedTab : firstNonEmpty
  const allEmpty = firstNonEmpty === null

  const tabs: Tab[] = [
    {
      label: 'Your schedule',
      value: 'schedule',
      badge: scheduleCount > 0 ? scheduleCount : '-',
      disabled: scheduleCount === 0,
    },
    {
      label: 'Incomplete',
      value: 'incomplete',
      badge: incompleteCount > 0 ? incompleteCount : '-',
      disabled: incompleteCount === 0,
    },
    ...(showOnlineTab ?
      [{ label: 'Review', value: 'online', badge: onlineCount > 0 ? onlineCount : '-', disabled: onlineCount === 0 }]
    : []),
  ]

  return (
    <PageLayout>
      <DailyVisitProvider
        patientWithVisitInfo={patientWithVisitInfo}
        visitIsPhoneCallOnly={patientWithVisitInfo?.VisitMethod === VisitMethods.CALL}
        isPrepMode={isPrepMode}
        onStartVisitClickInVisit={({ isPhoneCall }) => {
          onStartVisitClick(
            {
              ...selectedVisit,
              reconnectVisitMethod: isPhoneCall ? VisitMethods.CALL : VisitMethods.VIDEO,
            } as WaitingRoomV2VisitListItemDoctorApiOutContract,
            true,
          )
        }}>
        {getIsDevToolsEnabled(false) && (
          <SilentErrorBoundary onError={reportDevToolsBoundaryError}>
            <Suspense fallback={null}>
              <LazyVisitLifecycleDebugger />
              <LazyDashboardStateInspector
                visitLists={visitLists}
                selectedVisit={selectedVisit}
                selectedVisitInformation={selectedVisitInformation}
                selectedPatientInformation={selectedPatientInformation}
                showVisitPopUp={showVisitPopUp}
                isPrepMode={isPrepMode}
                isLoadingVisit={isLoadingVisit}
              />
            </Suspense>
          </SilentErrorBoundary>
        )}
        <div className='tailwind-scoped' style={{ minWidth: 766, overflowX: 'auto' }}>
          <div className='flex w-full flex-col gap-sp-2 bg-[#ECEFF1]'>
            {showUnsupportedPlatformFor ?
              <UnsupportedPlatformPopUp
                media={showUnsupportedPlatformFor}
                onClose={() => setShowUnsupportedPlatformFor(undefined)}
                onEndVisit={() => {
                  if (selectedVisitInformation?.VisitId) {
                    endVisit({ visitId: selectedVisitInformation.VisitId })
                    setShowUnsupportedPlatformFor(undefined)
                  }
                }}
              />
            : null}
            {showHardwareDeniedFor || showPermissionDeniedFor ?
              <HardwarePermissionsPopUp
                type={showPermissionDeniedFor ? 'permissions' : 'hardware'}
                media={showPermissionDeniedFor || showHardwareDeniedFor || 'video'}
                onClose={() => {
                  setShowPermissionDeniedFor(undefined)
                  setShowHardwareDeniedFor(undefined)
                }}
              />
            : null}
            <div>
              <Heading level='h1' className='text-text-dark'>
                Welcome{firstName ? `, ${firstName}` : ''}
              </Heading>
              <Paragraph className='text-text-medium'>{moment().format('dddd, MMMM D')}</Paragraph>
            </div>
            <AnnouncementPlacement />
            <div className='flex flex-col gap-sp-2'>
              {isWaitingRoomLoading ?
                <div
                  role='status'
                  aria-label='Loading visits'
                  className='flex min-h-[70vh] w-full items-center justify-center'>
                  <Spinner style={{ width: 48, height: 48 }} className='animate-spin text-text-light' />
                </div>
              : <>
                  <Tabset
                    tabs={tabs}
                    activeTab={activeTab ?? ''}
                    onTabChange={(v) => {
                      if (isQueueTabValue(v)) setSelectedTab(v)
                    }}
                  />
                  {allEmpty ?
                    <AllSetEmptyState />
                  : <>
                      {activeTab === 'schedule' && (
                        <ScheduleSection
                          hideHeader
                          list={scheduleList}
                          nextVisit={nextVisit}
                          justChangeTags={justChangeTags}
                          dismissedVisits={dismissedVisits}
                          onPrepVisitClick={onPrepVisitClick}
                          onDismissVisit={dismissVisit}
                        />
                      )}
                      {activeTab === 'incomplete' && (
                        <IncompleteSection hideHeader list={pendingList} onPrepVisitClick={onPrepVisitClick} />
                      )}
                      {activeTab === 'online' && (
                        <OnlineVisitsSection
                          list={visibleOnlineVisitsList}
                          onStartVisit={asyncStart.requestStart}
                          onContinueVisit={onContinueAsyncVisitClick}
                          optimisticStartIds={optimisticStartIds}
                          isStarting={asyncStart.isStarting}
                        />
                      )}
                    </>
                  }
                </>
              }
            </div>
            {showVisitPopUp ?
              <VisitPopUp
                onClose={() => {
                  setIsPrepMode(false)
                  onCloseVisitPopUp()
                }}
                upcomingVisits={upcomingVisits}
                onDeferred={handleVisitDeferred}
                onCompleted={handleVisitCompleted}
              />
            : null}
            <ReadyToStartVisitPopup
              isOpen={asyncStart.isConfirmOpen}
              onClose={asyncStart.cancel}
              onConfirm={asyncStart.confirmStart}
              isConfirming={asyncStart.isStarting}
            />
          </div>
        </div>
      </DailyVisitProvider>
      {isLoadingVisit && !showVisitPopUp ?
        <div className='tailwind-scoped' style={{ position: 'fixed', inset: 0, zIndex: 999999999 }}>
          <RoomLoadingSpinner contained />
        </div>
      : null}
    </PageLayout>
  )
}

export default Dashboard
